import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import { Button, Stack } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useState } from "react";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { PortalRole } from "../../../../api/types/accessTypes";
import { portalRoleCaptionMap } from "../../../../utilities/enumCaptions";
import type { CommunicationMatrixEntityType, CommunicationMatrixModel } from "./matrixHelper";
import { isEmptyInvestorContact } from "./matrixHelper";

interface Props {
  entityType: CommunicationMatrixEntityType;
  permissions: CommunicationMatrixModel[];
  onFilterChange: (items: CommunicationMatrixModel[]) => void;
}

interface FilterState {
  search: string;
  fundNames: string[];
  investorNames: string[];
  roleNames: string[];
}

const getRoleNames = (roles: PortalRole[]) =>
  roles.length > 0 ? roles.map((role) => portalRoleCaptionMap[role]) : "None";

const CommunicationMatrixFilters = ({
  permissions,
  entityType,
  onFilterChange,
  children,
}: PropsWithChildren<Props>) => {
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    fundNames: [],
    investorNames: [],
    roleNames: [],
  });

  const updateFiltersState = (update: Partial<FilterState>) => setFilters({ ...filters, ...update });

  useEffect(() => {
    const filteredPermissions = getFilteredItems(permissions, filters);
    onFilterChange(filteredPermissions);
  }, [filters, onFilterChange, permissions]);

  const onSearch = useDebounce((search: string) => updateFiltersState({ search: search.toLowerCase() }), 300);

  const clearFilters = () => updateFiltersState({ fundNames: [], roleNames: [], investorNames: [] });

  const updatePortalRolesFilters = (rolesNames: string[]) => updateFiltersState({ roleNames: rolesNames });

  const fundNames = useMemo(() => distinct(permissions.map((permission) => permission.fundName)), [permissions]);

  const investorNames = useMemo(
    () => distinct(permissions.map((permission) => permission.investorName)),
    [permissions]
  );

  const availableRoleNames = useMemo(
    () =>
      distinct(
        permissions
          .filter((m) => !isEmptyInvestorContact(m))
          .map((permission) => getRoleNames(permission.roles))
          .flatMap((r) => r)
      ),
    [permissions]
  );

  return (
    <Stack direction={"row"} spacing={1.5} sx={{ alignItems: "center" }}>
      <Stack direction={"row"} spacing={1.5} sx={{ width: 0, flexGrow: 1 }}>
        {entityType !== "Fund" && (
          <MultiSelectFilter
            sortOptions
            placeholder="Funds"
            searchable
            options={fundNames}
            getOptionValue={(name) => name}
            getOptionLabel={(name) => name}
            selectedValues={filters.fundNames}
            onChange={(fundNames) => updateFiltersState({ fundNames })}
            StartIcon={CompaniesIcon}
          />
        )}
        {(entityType === "Fund" || entityType === "Contact") && (
          <MultiSelectFilter
            sortOptions
            placeholder="Investors"
            options={investorNames}
            getOptionValue={(name) => name}
            getOptionLabel={(name) => name}
            selectedValues={filters.investorNames}
            onChange={(investorNames) => updateFiltersState({ investorNames })}
            StartIcon={PersonOutlineRoundedIcon}
          />
        )}
        {availableRoleNames.length > 0 && (
          <MultiSelectFilter
            sortOptions
            placeholder="Role"
            options={availableRoleNames}
            getOptionValue={(name) => name}
            getOptionLabel={(name) => name}
            selectedValues={filters.roleNames}
            onChange={updatePortalRolesFilters}
            StartIcon={SupervisorAccountOutlinedIcon}
          />
        )}

        {(filters.fundNames.length > 0 || filters.roleNames.length > 0 || filters.investorNames.length > 0) && (
          <Button onClick={clearFilters}>Clear all</Button>
        )}
      </Stack>
      <Stack direction={"row"} spacing={1.5}>
        <SearchField onSearch={onSearch} />
        {children}
      </Stack>
    </Stack>
  );
};

export default CommunicationMatrixFilters;

const getFilteredItems = (permissions: CommunicationMatrixModel[], filters: FilterState) => {
  const { search, fundNames, investorNames, roleNames } = filters;

  return permissions.filter((permission) => {
    const showBySearch =
      permission.investorName.toLowerCase().includes(search) ||
      permission.fundName.toLowerCase().includes(search) ||
      permission.contactName.toLowerCase().includes(search) ||
      (permission.contactEmail ?? "").toLowerCase().includes(search);

    const showByFund = !fundNames.length || fundNames.includes(permission.fundName);
    const showByInvestor = !investorNames.length || investorNames.includes(permission.investorName);
    const showByRole =
      roleNames.length === 0 || roleNames.some((name) => getRoleNames(permission.roles).includes(name));

    return showBySearch && showByFund && showByRole && showByInvestor;
  });
};
